import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { NavigateFunction } from "react-router-dom";
import {
  apiErrorMessage,
  getAdminOperationsOverview,
  normalizeResponseData
} from "../../core/api/adminApi";
import { useAuth } from "../../core/auth/AuthContext";
import type {
  AdminCollectionItem,
  AdminFollowRequestItem,
  AdminOperationsOverview,
  AdminPlaceListActivityItem
} from "../../shared/types/admin";
import { resolveAvatarSrc } from "../../shared/ui/avatar";

function formatDate(value?: string): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("de-DE");
}

function formatNumber(value: number | undefined): string {
  return (value ?? 0).toLocaleString("de-DE");
}

function ActorLink({
  userId,
  label,
  navigate
}: {
  userId?: string;
  label?: string;
  navigate: NavigateFunction;
}) {
  if (!userId) {
    return <span>{label || "-"}</span>;
  }

  return (
    <button
      type="button"
      className="link-button"
      onClick={() => navigate(`/users/${encodeURIComponent(userId)}`)}
    >
      {label || userId}
    </button>
  );
}

function PlaceListActivityCard({
  title,
  items,
  emptyLabel,
  navigate
}: {
  title: string;
  items: AdminPlaceListActivityItem[];
  emptyLabel: string;
  navigate: NavigateFunction;
}) {
  return (
    <div className="card">
      <div className="section-header">
        <label>{title}</label>
        <button type="button" className="tertiary" onClick={() => navigate("/wecommendations")}>
          Öffnen
        </button>
      </div>
      <div className="item-list">
        {items.map((item) => (
          <div key={item.id || `${item.userId}-${item.placeId}-${item.createdDate}`} className="item-row">
            <div className="item-title">{item.placeName || "(ohne Ort)"}</div>
            <div className="item-meta">
              <ActorLink
                userId={item.userId}
                label={item.userName || item.userEmail || item.userId}
                navigate={navigate}
              />{" "}
              · {formatDate(item.createdDate)}
            </div>
            <div className="item-meta">{item.placeAddress || "-"}</div>
            {item.userComment ? <div className="item-comment">"{item.userComment}"</div> : null}
            {item.placeType?.length ? (
              <div className="item-tags">
                {item.placeType.map((tag) => (
                  <span key={tag} className="item-tag">
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        ))}
        {items.length === 0 ? <div className="item-row muted">{emptyLabel}</div> : null}
      </div>
    </div>
  );
}

function FollowRequestsCard({
  items,
  navigate
}: {
  items: AdminFollowRequestItem[];
  navigate: NavigateFunction;
}) {
  return (
    <div className="card">
      <div className="section-header">
        <label>Neue Follow Requests</label>
        <button type="button" className="tertiary" onClick={() => navigate("/friendships")}>
          Öffnen
        </button>
      </div>
      <div className="item-list">
        {items.map((item) => (
          <div key={item.id || `${item.requester?.userId}-${item.requested?.userId}-${item.requestedOn}`} className="item-row">
            <div className="item-title">
              {item.requester?.name || item.requester?.userId || "Unbekannt"} →{" "}
              {item.requested?.name || item.requested?.userId || "Unbekannt"}
            </div>
            <div className="item-meta">
              Status {item.status || "-"} · {formatDate(item.requestedOn)}
            </div>
            <div className="item-meta">
              Sender:{" "}
              <ActorLink
                userId={item.requester?.userId}
                label={item.requester?.email || item.requester?.userId}
                navigate={navigate}
              />
            </div>
            <div className="item-meta">
              Empfänger:{" "}
              <ActorLink
                userId={item.requested?.userId}
                label={item.requested?.email || item.requested?.userId}
                navigate={navigate}
              />
            </div>
          </div>
        ))}
        {items.length === 0 ? <div className="item-row muted">Keine Follow Requests im Zeitraum.</div> : null}
      </div>
    </div>
  );
}

function CollectionsCard({
  items,
  navigate
}: {
  items: AdminCollectionItem[];
  navigate: NavigateFunction;
}) {
  return (
    <div className="card">
      <div className="section-header">
        <label>Neue Collections</label>
        <button type="button" className="tertiary" onClick={() => navigate("/collections")}>
          Öffnen
        </button>
      </div>
      <div className="item-list">
        {items.map((item) => (
          <div key={item.id || `${item.userId}-${item.createdDate}`} className="item-row">
            <div className="item-title">{item.title || "(ohne Titel)"}</div>
            <div className="item-meta">
              <ActorLink
                userId={item.userId}
                label={item.userName || item.userEmail || item.userId}
                navigate={navigate}
              />{" "}
              · {formatDate(item.createdDate)}
            </div>
            <div className="item-meta">
              Typ {item.type || "-"} · {formatNumber(item.placesCount)} Orte
            </div>
            {item.placeNames?.length ? (
              <div className="item-tags">
                {item.placeNames.slice(0, 4).map((placeName) => (
                  <span key={placeName} className="item-tag">
                    {placeName}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        ))}
        {items.length === 0 ? <div className="item-row muted">Keine Collections im Zeitraum.</div> : null}
      </div>
    </div>
  );
}

export function DashboardPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState<AdminOperationsOverview | undefined>();
  const [trendDays, setTrendDays] = useState(14);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("Bereit.");

  const loadDashboard = async () => {
    setLoading(true);
    setError("");
    setInfo("Lade Operations-Übersicht...");
    try {
      const response = await getAdminOperationsOverview(token, {
        days: trendDays,
        limit: 10
      });
      setOverview(normalizeResponseData(response));
      setInfo("Operations-Übersicht aktualisiert.");
    } catch (e) {
      setError(apiErrorMessage(e));
      setInfo("Fehler beim Laden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadDashboard();
  }, [trendDays]);

  const trendMax = useMemo(() => {
    const values = [
      ...(overview?.signups || []),
      ...(overview?.wecommendations || []),
      ...(overview?.bucketlistEntries || []),
      ...(overview?.followRequests || []),
      ...(overview?.collections || [])
    ];
    return Math.max(1, ...values);
  }, [overview]);

  return (
    <section>
      <div className="view-headline">
        <h1>Operations Console</h1>
        <p className="muted">Support, Nutzerverhalten und Marketing-Snapshot aus den produktiven Postgres-Daten.</p>
      </div>

      <div className="card">
        <div className="section-header">
          <div className="muted">{info}</div>
          <div className="dashboard-controls">
            <select
              value={trendDays}
              onChange={(event) => setTrendDays(Number(event.target.value))}
            >
              <option value={7}>7 Tage</option>
              <option value={14}>14 Tage</option>
              <option value={30}>30 Tage</option>
              <option value={60}>60 Tage</option>
              <option value={90}>90 Tage</option>
            </select>
            <button type="button" className="primary" onClick={() => void loadDashboard()} disabled={loading}>
              {loading ? "Aktualisiere..." : "Aktualisieren"}
            </button>
          </div>
        </div>

        {error ? <div className="error-box">{error}</div> : null}

        <div className="dashboard-kpi-grid">
          <div className="kpi-card">
            <div className="kpi-label">App aktiv / neue User</div>
            <div className="kpi-value">
              {formatNumber(overview?.snapshot?.activeUsers)} / {formatNumber(overview?.snapshot?.newUsers)}
            </div>
            <div className="kpi-sub">Aktive User = letzter `last_activity` im Zeitraum</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">WEC Autoren / Einträge</div>
            <div className="kpi-value">
              {formatNumber(overview?.snapshot?.wecommendationAuthors)} / {formatNumber(overview?.snapshot?.wecommendations)}
            </div>
            <div className="kpi-sub">Wer hat eine Wecommendation geschrieben?</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Wunschliste User / Einträge</div>
            <div className="kpi-value">
              {formatNumber(overview?.snapshot?.wishlistUsers)} / {formatNumber(overview?.snapshot?.wishlistEntries)}
            </div>
            <div className="kpi-sub">Wer hat Orte auf die Wunschliste gesetzt?</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Follow-Sender / Requests</div>
            <div className="kpi-value">
              {formatNumber(overview?.snapshot?.followRequestSenders)} / {formatNumber(overview?.snapshot?.followRequests)}
            </div>
            <div className="kpi-sub">Wer hat Freundschaftsanfragen versendet?</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Collection Creator / Collections</div>
            <div className="kpi-value">
              {formatNumber(overview?.snapshot?.collectionCreators)} / {formatNumber(overview?.snapshot?.collections)}
            </div>
            <div className="kpi-sub">Wer hat Collections erstellt?</div>
          </div>
        </div>

        {overview?.warnings?.length ? (
          <div className="action-status info">
            {overview.warnings.map((warning) => (
              <div key={warning}>{warning}</div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="section-header">
          <label>Entwicklung im Zeitraum</label>
          <span className="muted">Signups / WEC / Wunschliste / Follow Requests / Collections</span>
        </div>
        <div className="trend-grid">
          {(overview?.labels || []).map((label, index) => {
            const signups = (overview?.signups || [])[index] || 0;
            const wecommendations = (overview?.wecommendations || [])[index] || 0;
            const bucketlistEntries = (overview?.bucketlistEntries || [])[index] || 0;
            const followRequests = (overview?.followRequests || [])[index] || 0;
            const collections = (overview?.collections || [])[index] || 0;

            return (
              <div key={label} className="trend-col">
                <div className="trend-bars">
                  <div
                    className="trend-bar signups"
                    style={{ height: `${Math.max(4, (signups / trendMax) * 72)}px` }}
                    title={`Signups: ${signups}`}
                  />
                  <div
                    className="trend-bar wco"
                    style={{ height: `${Math.max(4, (wecommendations / trendMax) * 72)}px` }}
                    title={`Wecommendations: ${wecommendations}`}
                  />
                  <div
                    className="trend-bar bucket"
                    style={{ height: `${Math.max(4, (bucketlistEntries / trendMax) * 72)}px` }}
                    title={`Wunschliste: ${bucketlistEntries}`}
                  />
                  <div
                    className="trend-bar follow"
                    style={{ height: `${Math.max(4, (followRequests / trendMax) * 72)}px` }}
                    title={`Follow Requests: ${followRequests}`}
                  />
                  <div
                    className="trend-bar collections"
                    style={{ height: `${Math.max(4, (collections / trendMax) * 72)}px` }}
                    title={`Collections: ${collections}`}
                  />
                </div>
                <div className="trend-label">
                  {label.slice(5)}
                  <br />
                  <span className="muted">
                    {signups}/{wecommendations}/{bucketlistEntries}/{followRequests}/{collections}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="section-header">
          <label>Aktive User im Zeitraum</label>
          <button type="button" className="tertiary" onClick={() => navigate("/users")}>
            Zur User-Verwaltung
          </button>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Avatar</th>
                <th>Name</th>
                <th>E-Mail</th>
                <th>City</th>
                <th>Last Active</th>
                <th>WEC</th>
                <th>Wishlist</th>
                <th>Collections</th>
              </tr>
            </thead>
            <tbody>
              {(overview?.activeUsers || []).map((entry) => {
                const avatarSrc = resolveAvatarSrc(entry.userId, entry.avatar);
                return (
                  <tr key={entry.userId || entry.userUuid}>
                    <td>
                      {avatarSrc ? (
                        <img className="avatar" src={avatarSrc} alt="avatar" />
                      ) : (
                        <div className="avatar placeholder" />
                      )}
                    </td>
                    <td>
                      <ActorLink
                        userId={entry.userId}
                        label={entry.name || entry.userId || "(ohne Name)"}
                        navigate={navigate}
                      />
                    </td>
                    <td>{entry.email || "-"}</td>
                    <td>{entry.city || "-"}</td>
                    <td>{formatDate(entry.lastActiveAt)}</td>
                    <td>{formatNumber(entry.wecommendations)}</td>
                    <td>{formatNumber(entry.bucketlistEntries)}</td>
                    <td>{formatNumber(entry.collections)}</td>
                  </tr>
                );
              })}
              {(overview?.activeUsers || []).length === 0 ? (
                <tr>
                  <td colSpan={8} className="muted">
                    Keine aktiven User im Zeitraum gefunden.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <div className="list-grid" style={{ marginTop: 16 }}>
        <PlaceListActivityCard
          title="Neue Wecommendations"
          items={overview?.recentWecommendations || []}
          emptyLabel="Keine Wecommendations im Zeitraum."
          navigate={navigate}
        />
        <PlaceListActivityCard
          title="Neue Wunschlisten-Einträge"
          items={overview?.recentBucketlistEntries || []}
          emptyLabel="Keine Wunschlisten-Einträge im Zeitraum."
          navigate={navigate}
        />
        <FollowRequestsCard items={overview?.recentFollowRequests || []} navigate={navigate} />
        <CollectionsCard items={overview?.recentCollections || []} navigate={navigate} />
      </div>
    </section>
  );
}
